import { action, computed, observable } from "mobx";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { RouteComponentProps } from "react-router-dom";
import { AddButton } from "../Buttons/AddButton";
import { ButtonGroup } from "../Buttons/ButtonGroup";
import { CloseButton } from "../Buttons/CloseButton";
import { DeleteButton } from "../Buttons/DeleteButton";
import { EditButton } from "../Buttons/EditButton";
import { SaveButton } from "../Buttons/SaveButton";
import { SelectionButton } from "../Buttons/SelectionButton";
import { ShowAreaAsideButton } from "../Buttons/ShowAreaAsideButton";
import * as DocumentManagement from "../DocumentManagement";
import { AreaBody } from "../General/AreaBody";
import { ContentGroup } from "../General/ContentGroup";
import { LanguagePicker } from "../General/LanguagePicker";
import { SelectDocumentDialog } from "../General/SelectDocumentDialog";
import { Toolbar } from "../General/Toolbar";
import { WizardBox, WizardBoxType } from "../General/WizardBox";
import { InputChoiseComponent } from "../Inputs/InputChoiseComponent";
import { InputDateComponent } from "../Inputs/InputDateComponent";
import { InputDocumentLinkComponent } from "../Inputs/InputDocumentLinkComponent";
import { InputHierarchyComponent } from "../Inputs/InputHierarchyComponent";
import { InputNumberComponent } from "../Inputs/InputNumberComponent";
import { InputTextComponent } from "../Inputs/InputTextComponent";
import * as Shared from "../Shared";
import * as UiStore from "../States/AreaUiInfo";
import { AppStore } from "../Stores/AppStore";
import { DialogResult, DialogStore } from "../Stores/DialogStore";
import { DocumentMetaDataWizardStore } from "../Stores/DocumentMetaDataWizardStore";
import { DocumentStoreImpl } from "../Stores/DocumentStore";
import { NotificationStore } from "../Stores/NotificationStore";
import { Strings } from "../Stores/StringsStore";
import { Urls } from "../Urls";
import * as Utilities from "../Utilities";
import { DocumentEditFiles } from "./DocumentEdit.Files";
import { DocumentMetaSelection } from "./DocumentMetaSelection";
import { DocumentPrivilegesEdit } from "./DocumentPrivilegesEdit";
import { DocumentPrivilegeSelection } from "./DocumentPrivilegeSelection";
import { HierarchyMetaValueSelection } from "./HierarchyMetaValueSelection";
import { SelectCheckbox } from "./SelectCheckbox";
const parentUrl = "/collections";

interface IDocumentEditProps
	extends RouteComponentProps<IDocumentEditPropsParams>,
		UiStore.IAreaUiInfo {
	documentStore?: DocumentStoreImpl;
}

interface IDocumentEditPropsParams {
	collection: string;
	id: string;
}

@inject("documentStore")
@inject("areaUiInfo")
@observer
export class DocumentEdit extends React.Component<IDocumentEditProps, {}> {
	store: DocumentStoreImpl;
	@observable selectedLanguage: string;
	@observable fileUploadInProgress = false;
	@observable hash = "";

	componentWillMount() {
		this.store = this.props.documentStore;
		this.store.metaDataWizardStore.onConfirmExternal = this.onAddMetas;
		this.store.privilegesWizardStore.onConfirmExternal =
			this.onAddPrivileges;
	}

	componentDidMount() {
		this.init(this.props);
	}

	componentWillUpdate(nextProps: IDocumentEditProps) {
		if (
			this.props.match.params.collection !==
				nextProps.match.params.collection ||
			this.props.match.params.id !== nextProps.match.params.id
		) {
			this.init(nextProps);
		}
	}

	init(props: IDocumentEditProps) {
		AppStore.isBusyModelLoading = true;
		AppStore.isDirtyMode = false;
		AppStore.isDirtyChange = false;
		this.store.addedPrivileges = [];
		this.store.removedDocumentPrivileges = [];
		let uiLang = Utilities.getUiLanguage();
		if (!uiLang) {
			uiLang = AppStore.context.userInterfaceLanguages[0].name;
		}
		this.selectedLanguage = uiLang;
		this.store.metaDataWizardStore.language = this.selectedLanguage;
		this.getModel(props);
		this.setTitle();
	}

	@action setTitle = () => {
		if (this.store.model.isNew) {
			AppStore.setTitle(Strings.documentManagement.documentEdit.addTitle);
		} else {
			AppStore.setTitle(
				Strings.documentManagement.documentEdit.documentTitle
			);
		}
	};

	@action setModel(model: DocumentManagement.DocumentDetails) {
		this.store.model = this.fillMissing(model);
		this.hash = JSON.stringify(this.store.model);
		this.store.tempStorageDocumentPrivileges = this.store.model.privileges;
		NotificationStore.clearMessage();
		AppStore.isBusyModelLoading = false;
	}

	@action getModel(props: IDocumentEditProps) {
		AppStore.isBusyModelLoading = true;
		let query = new DocumentManagement.DocumentDetailsQuery();
		query.guid = props.match.params.id;
		query.language = this.selectedLanguage;
		DocumentManagement.getDocumentAsync(query)
			.then((model) => {
				this.setModel(model);
			})
			.catch((error) => {
				DialogStore.showError(Strings.general.errorDialogTitle, error);
			});
	}

	@computed get canSave(): boolean {
		let canSave =
			!this.fileUploadInProgress &&
			this.store.model.name.value.trim() &&
			(JSON.stringify(this.store.model) !== this.hash ||
				this.store.addedPrivileges.some((privilege) =>
					privilege.privilegeMemberships.some(
						(item) =>
							item.isExplicitMemberOf || item.isImplicitMemberOf
					)
				));
		for (let i = 0; i < this.store.model.metas.length; i++) {
			let meta = this.store.model.metas[i];
			for (let j = 0; j < meta.values.length; j++) {
				if (!meta.values[j].isValid) {
					canSave = false;
				}
			}
		}
		AppStore.isDirtyMode = canSave;
		AppStore.isDirtyChange =
			this.hash !== JSON.stringify(this.store.model) ||
			this.store.addedPrivileges.length > 0;
		AppStore.saveOnLeave = this.saveOnDirtyLeave;
		return canSave;
	}

	get languageOptions() {
		let result: Shared.LanguageInfo[] = [];
		AppStore.context.contentLanguages
			.sort((a, b) => {
				if (a.caption < b.caption) return -1;
				if (a.caption > b.caption) return 1;
				return 0;
			})
			.forEach((lang) => result.push(lang));
		return result;
	}

	onLanguageSelect = (option: Shared.LanguageInfo) => {
		if (option.name !== this.selectedLanguage) {
			if (this.canSave) {
				DialogStore.show(
					Strings.general.keepOnPageDialogTitle,
					Strings.general.keepOnPageDialogText
				).then((result: DialogResult) => {
					if (result === DialogResult.Yes) {
						this.saveModel(false, () =>
							this.setLanguage(option.name)
						);
					}
					if (result === DialogResult.No) {
						this.setLanguage(option.name);
					}
				});
			} else {
				this.setLanguage(option.name);
			}
		}
	};

	private setLanguage(language: string) {
		this.selectedLanguage = language;
		this.store.metaDataWizardStore.language = language;
		this.getModel(this.props);
	}

	handleSaveUi = () => {
		this.saveModel(true);
	};

	saveOnDirtyLeave = (onSaveCompleted: () => void) => {
		this.saveModel(false, onSaveCompleted);
	};

	private saveModel(update: boolean, onSaveCompleted?: () => void) {
		if (this.canSave) {
			AppStore.isBusy = true;
			//Adding Selected privileges and existign privileges to one object for update the data
			let selectedprivileges =
				new DocumentManagement.DocumentPrivileges();
			selectedprivileges.documentId = this.props.match.params.id;
			this.store.model.privileges.privilegesForGroups.map((privilege) => {
				selectedprivileges.privilegesForGroups.push(privilege);
			});
			this.store.addedPrivileges.map((privilege) => {
				selectedprivileges.privilegesForGroups.push(privilege);
			});
			let updateModel = new DocumentManagement.DocumentDetailsUpdate();
			updateModel.privileges.documentId =
				this.props.match.params.collection;
			updateModel.privileges = selectedprivileges;
			updateModel.collectionId = this.props.match.params.collection;
			updateModel.guid = this.store.model.guid;
			updateModel.language = this.selectedLanguage;
			updateModel.lastModified = this.store.model.lastModified;
			updateModel.id = this.store.model.id.value;
			updateModel.name = this.store.model.name.value;
			updateModel.metas = this.store.getDocumentMetaUpdate;
			DocumentManagement.insertOrUpdateDocumentAsync(updateModel)
				.then((model) => {
					AppStore.isBusy = false;
					if (model.isValid) {
						NotificationStore.showSuccessMessage(
							Strings.general.saveSuccessMessage
						);
					} else {
						DialogStore.showError(
							Strings.general.errorDialogTitle,
							model.validationMessage
						);
					}
					if (update) {
						this.store.model = this.fillMissing(model);
						if (this.store.model.isValid) {
							this.hash = JSON.stringify(this.store.model);
							this.store.addedPrivileges = [];
							this.store.tempStorageDocumentPrivileges =
								this.store.model.privileges;
							this.store.removedDocumentPrivileges = [];
						}
					}
					if (onSaveCompleted) {
						onSaveCompleted();
					}
				})
				.catch((error) => {
					AppStore.isBusy = false;
					DialogStore.showError(
						Strings.general.errorDialogTitle,
						error
					);
				});
		}
	}

	handleDelete = () => {
		DialogStore.show(
			Utilities.format(
				Strings.general.dialogDeleteTitle,
				this.store.model.name.value
			),
			Utilities.format(
				Strings.general.dialogDeleteText,
				this.store.model.name.value
			)
		).then((result) => {
			if (result === DialogResult.Yes) {
				AppStore.isBusy = true;
				DocumentManagement.removeDocumentAsync({
					guid: this.store.model.guid,
					lastModified: this.store.model.lastModified,
				})
					.then(() => {
						AppStore.isBusy = false;
						NotificationStore.showSuccessMessage(
							Strings.general.deleteSuccessMessage
						);
						AppStore.history.replace(parentUrl);
					})
					.catch((error) => {
						AppStore.isBusy = false;
						DialogStore.showError(
							Strings.general.errorDialogTitle,
							error
						);
					});
			}
		});
	};

	handleCancel = () => {
		AppStore.keepOnPage(parentUrl, null, this.props.areaUiInfo.closeExpand);
	};

	handleChange = () => {
		NotificationStore.clearMessage();
	};

	//starts the wizard for adding document privileges.
	handleAddDocumentPrivilege = () => {
		this.store.privilegesWizardStore.open();
	};

	@action handlePrivilegeSelectionMode = () => {
		this.props.documentStore.togglePrivilegeSelectionMode();
	};

	@action handleMetaSelectionMode = () => {
		this.props.documentStore.toggleMetaSelectionMode();
	};

	refs: {
		[key: string]: React.Component;
		metaContentGroup: ContentGroup;
		privilegeContentGroup: ContentGroup;
		fileContentGroup: ContentGroup;
	};

	onAddFiles = () => {
		this.refs.fileContentGroup.collapsed = false;
	};

	onAddMetas = () => {
		this.refs.metaContentGroup.collapsed = false;
	};

	onAddPrivileges = () => {
		this.refs.privilegeContentGroup.collapsed = false;
	};

	fillMissing(model: DocumentManagement.DocumentDetails) {
		model.id.label = Strings.documentManagement.documentEdit.id;
		model.id.placeHolder =
			Strings.documentManagement.documentEdit.idPlaceHolder;
		model.name.label = Strings.documentManagement.documentEdit.name;
		model.name.regex = "[^s].{0,99}";
		if (!model.name.placeHolder) {
			model.name.placeHolder =
				Strings.documentManagement.documentEdit.namePlaceHolder;
		}
		return model;
	}

	private supportedMimetypes(): string {
		return ".epub,.pdf,.png,image/jpeg,.svg,.mp4,.docx,.docm,.pptx,.pptm,.xlsm,.xlsx";
	}

	@computed get areaAsideButtonTooltip() {
		return this.props.areaUiInfo.isCollapsed
			? Strings.documentManagement.collectionEdit
					.areaAsideButtonCollapsedTooltip
			: Strings.documentManagement.collectionEdit
					.areaAsideButtonExpandedTooltip;
	}

	onFileManagementButtonClick = () => {
		let parts = window.location.href.split("?");
		let queryString = parts.length > 1 ? "?" + parts[1] : "";
		let url = `${Urls.collections}/${this.props.match.params.collection}/documents/${this.props.match.params.id}/files/${AppStore.context.sessionDetails.uiLanguage}${queryString}`;
		AppStore.keepOnPage(url, true);
	};

	render() {
		let lang = AppStore.context.contentLanguages.filter(
			(l) => l.name === this.selectedLanguage
		)[0];
		return (
			<AreaBody
				loading={AppStore.isBusyModelLoading}
				areaUiInfo={this.props.areaUiInfo}
			>
				<Toolbar>
					<ShowAreaAsideButton title={this.areaAsideButtonTooltip} />
					<SaveButton
						onClick={this.handleSaveUi}
						disabled={!this.canSave}
						testId="saveButton"
					/>
					<DeleteButton
						onClick={this.handleDelete}
						testId="deleteButton"
					/>
					<LanguagePicker
						options={this.languageOptions}
						value={this.selectedLanguage}
						onChange={this.onLanguageSelect}
					/>
					<span className="buttongroup is--buttongroup-right">
						<CloseButton
							onClick={this.handleCancel}
							testId="closeButton"
						/>
					</span>
				</Toolbar>
				<div className="area-grid__body-content">
					<div className="content-main">
						<h1>
							{this.store.model.isNew
								? Strings.documentManagement.documentEdit
										.addTitle
								: Strings.documentManagement.documentEdit
										.documentTitle}
						</h1>
						<InputTextComponent
							model={this.store.model.id}
							onChange={this.handleChange}
							testId="idInput"
						/>
						<InputTextComponent
							model={this.store.model.name}
							onChange={this.handleChange}
							testId="nameInput"
							required={true}
						/>
						<ContentGroup
							ref="fileContentGroup"
							caption={
								Strings.documentManagement.documentEdit
									.filesTitle
							}
							uiContext={this.props.areaUiInfo}
							testId="documentFiles"
						>
							<ButtonGroup>
								<EditButton
									title={
										Strings.documentManagement.documentEdit
											.fileManagementButtonTooltip
									}
									onClick={this.onFileManagementButtonClick}
									testId="fileManagementButton"
									disabled={this.store.model.isNew}
								/>
							</ButtonGroup>
							<DocumentEditFiles
								files={this.store.model.files}
								isNew={this.store.model.isNew}
								params={this.props.match.params}
							/>
						</ContentGroup>
						<ContentGroup
							ref="metaContentGroup"
							caption={Strings.metaManagement.metas.title}
							uiContext={this.props.areaUiInfo}
							testId="documentMetaData"
						>
							<ButtonGroup>
								<AddButton
									title={
										Strings.documentManagement.documentEdit
											.metasAddButtonTooltip
									}
									onClick={
										this.store.metaDataWizardStore.open
									}
									testId="documentMetasAddButton"
								/>
								<CloseButton
									title={
										Strings.documentManagement.documentEdit
											.metasDeleteButtonTooltip
									}
									hide={
										!this.props.documentStore
											.metaSelectionMode
									}
									disabled={!this.store.hasSelectedMetaItems}
									onClick={
										this.props.documentStore
											.handleMetasDelete
									}
									testId="metaDeleteButton"
								/>
								<SelectionButton
									title={
										Strings.documentManagement.documentEdit
											.metasSelectionButtonTooltip
									}
									onClick={this.handleMetaSelectionMode}
									pressed={
										this.props.documentStore
											.metaSelectionMode
									}
									testId="metaSelectionButton"
								/>
								<span className="tinytool__splitter"></span>
							</ButtonGroup>
							<DocumentMetas
								selectedLanguage={lang && lang.name}
								languageCaption={lang && lang.caption}
								region={lang && lang.region}
							/>
						</ContentGroup>

						<ContentGroup
							ref="privilegeContentGroup"
							caption={
								Strings.documentManagement.collections
									.privilegesCaption
							}
							uiContext={this.props.areaUiInfo}
							testId="documentPrivileges"
						>
							<ButtonGroup>
								<AddButton
									title={
										Strings.documentManagement.documentEdit
											.privilegesAddButtonTooltip
									}
									onClick={this.handleAddDocumentPrivilege}
									testId="documentPrivilegesAddButton"
								/>
								<CloseButton
									title={
										Strings.documentManagement.documentEdit
											.privilegesDeleteButtonTooltip
									}
									hide={
										!this.props.documentStore
											.privilegeSelectionMode
									}
									disabled={
										!this.store.hasSelectedPrivilegeItems
									}
									onClick={
										this.props.documentStore
											.handlePrivilegesDelete
									}
									testId="privilegeDeleteButton"
								/>
								<SelectionButton
									title={
										Strings.documentManagement.documentEdit
											.privilegesSelectionButtonTooltip
									}
									onClick={this.handlePrivilegeSelectionMode}
									pressed={
										this.props.documentStore
											.privilegeSelectionMode
									}
									testId="privilegeSelectionButton"
								/>
								<span className="tinytool__splitter"></span>
							</ButtonGroup>
							{!AppStore.isBusyModelLoading ? (
								<DocumentPrivilegesEdit
									collectionId={
										this.props.match.params.collection
									}
									key={
										"privilege-" +
										this.props.match.params.id
									}
									document={this.store.model}
								/>
							) : null}
						</ContentGroup>
						{this.store.privilegesWizardStore.isOpen ? (
							<WizardBox
								context={this.store.privilegesWizardStore}
								type={WizardBoxType.ConfirmReject}
							>
								<DocumentPrivilegeSelection
									collectionID={
										this.props.match.params.collection
									}
									documentID={this.props.match.params.id}
									documentStore={this.store}
								/>
							</WizardBox>
						) : null}
					</div>
				</div>
				{this.store.metaDataWizardStore.isOpen ? (
					<WizardBox
						context={this.store.metaDataWizardStore}
						type={WizardBoxType.ConfirmReject}
					>
						<DocumentMetaSelection />
					</WizardBox>
				) : null}
				{this.store.hierarchyMetaValueWizardStore.isOpen ? (
					<WizardBox
						context={this.store.hierarchyMetaValueWizardStore}
						type={WizardBoxType.Hierarchy}
					>
						<HierarchyMetaValueSelection
							values={this.store.model.hierarchy}
							updateConfirmButton={() => {
								if (!this.store.selectedHierarchyMetaValue) {
									this.store.hierarchyMetaValueWizardStore.confirmEnabled =
										false;
								} else {
									this.store.hierarchyMetaValueWizardStore.confirmEnabled =
										true;
								}
							}}
						/>
					</WizardBox>
				) : null}
				{this.store.metaDataSelectDocumentsWizardStore.isOpen ? (
					<WizardBox
						context={this.store.metaDataSelectDocumentsWizardStore}
						type={WizardBoxType.DocumentLink}
						className="select-document"
					>
						<SelectDocumentDialog
							updateConfirmButton={() =>
								(this.store.metaDataSelectDocumentsWizardStore.confirmEnabled =
									this.store.addedDocuments.length > 0)
							}
						/>
					</WizardBox>
				) : null}
			</AreaBody>
		);
	}
}

interface IDocumentMetaProps {
	selectedLanguage: string;
	languageCaption: string;
	region: string;
	documentStore?: DocumentStoreImpl;
}

@inject("documentStore")
@observer
class DocumentMetas extends React.Component<IDocumentMetaProps, {}> {
	store: DocumentStoreImpl;

	componentWillMount() {
		this.store = this.props.documentStore;
		this.store.handleMetasDelete = this.handleMetasDelete;
	}

	handleNoMetaSpanClick = () => {
		this.store.metaDataWizardStore.open();
	};

	@action handleMetasDelete = () => {
		this.props.documentStore.selectedDocumentMetas.forEach((m) => {
			this.store.model.metas.remove(m);
			this.store.addedDocumentMetas.remove(m.metaGuid);
		});
		this.props.documentStore.selectedDocumentMetas.clear();
	};

	@computed get metaListClass(): string {
		return this.props.documentStore.metaSelectionMode
			? "documentMetaEdit is--selection"
			: "documentMetaEdit";
	}

	render() {
		return this.store.model.metas.length > 0 ? (
			<div className={this.metaListClass}>
				{this.store.model.metas.map((metaItem) => {
					return (
						<DocumentMetaItem
							key={metaItem.metaGuid}
							meta={metaItem}
							selectedLanguage={this.props.selectedLanguage}
							languageCaption={this.props.languageCaption}
							region={this.props.region}
							testName="documentMeta"
							testId={metaItem.caption}
						/>
					);
				})}
			</div>
		) : (
			<div
				className="inside__note is--clickable-note"
				onClick={this.handleNoMetaSpanClick}
			>
				<span>
					{
						Strings.documentManagement.documentEdit
							.documentEmptyMetaResultText
					}
				</span>
			</div>
		);
	}
}

interface IDocumentMetaItemProps extends IDocumentMetaProps {
	meta: DocumentManagement.DocumentMeta;
	selectedLanguage: string;
	languageCaption: string;
	region: string;
	testId?: string;
	testName?: string;
}

@inject("documentStore")
@observer
class DocumentMetaItem extends React.Component<IDocumentMetaItemProps, {}> {
	store: DocumentStoreImpl;
	@observable isNew: boolean = false;

	componentWillMount() {
		this.store = this.props.documentStore;
	}

	@action handleAddMultipleMeta = (metaGuid: string) => {
		const currentMeta = this.store.model.metas.find((meta) => {
			return meta.metaGuid === metaGuid;
		});
		DocumentManagement.getNewDocumentMetaAsync(metaGuid)
			.then((documentMeta) => {
				currentMeta.values.push(documentMeta.values[0]);
			})
			.catch((error) =>
				DialogStore.showError(Strings.general.errorDialogTitle, error)
			);
	};

	handleEditMeta = (metaId: string) => {
		let path = `${Urls.metas}/${metaId}`;
		AppStore.keepOnPage(path, true);
	};

	handleEditDocument = (documentId: string, collectionId: string) => {
		DocumentManagement.getCollectionHierarchyAsync(collectionId).then(
			(result: DocumentManagement.CollectionHierarchy) => {
				let path = `${Urls.collections}/${collectionId}/documents/${documentId}`;
				let treepath = `?treepath=${result.parentGuids.join(",")}`;
				if (treepath) {
					treepath += `,${collectionId}`;
					path += treepath;
				}
				AppStore.keepOnPage(path, true);
			}
		);
	};

	handleDeleteMeta = (metaValue: DocumentManagement.DocumentMetaValue) => {
		this.props.meta.values.remove(metaValue);
		if (this.props.meta.values.length === 0) {
			this.store.model.metas.remove(this.props.meta);
		}
	};

	handleChange = () => {
		DocumentMetaDataWizardStore.checkMetaValues(this.props.meta);
	};

	@computed get isSelected(): boolean {
		return this.props.documentStore.selectedDocumentMetas.some(
			(m) => m.metaGuid === this.props.meta.metaGuid
		);
	}

	@action handleClickItem = (e: React.MouseEvent<any>) => {
		if (this.props.documentStore.metaSelectionMode) {
			if (
				!this.props.documentStore.selectedDocumentMetas.remove(
					this.props.meta
				)
			) {
				this.props.documentStore.selectedDocumentMetas.push(
					this.props.meta
				);
			}
			e.preventDefault();
		}
	};

	clickValue = (metaValue: DocumentManagement.DocumentMetaValue) => {
		if (this.props.meta.inputType === Shared.InputType.InputDocumentLink) {
			this.store.existingDocumentLinkMetaValue = metaValue;
		}
		if (this.props.meta.inputType === Shared.InputType.InputHierarchy) {
			this.store.existingHierarchyMetaValue = metaValue;
		}
	};

	onUpdateValue = () => {
		this.isNew = true;
	};

	render() {
		return (
			<fieldset key={this.props.meta.metaGuid} className="various-value">
				<legend className="documentMetaEditTitle">
					<SelectCheckbox
						onClick={this.handleClickItem.bind(this)}
						checked={this.isSelected}
						disabled={false}
					/>
					{this.props.meta.caption}
					{this.props.meta.languageDependent ? (
						this.props.region.toLowerCase() == "001" ? (
							<img
								className="lang-dependent"
								title={this.props.languageCaption}
								src={
									"../../images/flags/" +
									this.props.region +
									".png"
								}
							/>
						) : (
							<div
								title={this.props.languageCaption}
								className={
									"flag flag-" +
									this.props.region.toLowerCase()
								}
							/>
						)
					) : null}
					<span className="documentMetaEditButton">
						<EditButton
							title={
								Strings.documentManagement.documentEdit
									.metasEditButtonTooltip
							}
							onClick={() =>
								this.handleEditMeta(this.props.meta.metaGuid)
							}
						/>
					</span>
					<span className="various-value__additional">
						<div className="tinytool">
							{this.props.meta.multiple ? (
								<AddButton
									title={
										Strings.documentManagement.documentEdit
											.metasAddValueButtonTooltip
									}
									onClick={this.handleAddMultipleMeta.bind(
										this,
										this.props.meta.metaGuid
									)}
								/>
							) : null}
						</div>
					</span>
				</legend>
				<ul>
					{this.props.meta.values.map((metaValue, index) => {
						let metaComponent: JSX.Element = null;
						switch (this.props.meta.inputType) {
							case Shared.InputType.InputText:
								metaComponent = (
									<InputTextComponent
										key={metaValue.guid + index + "text"}
										model={metaValue.valueText}
										onChange={this.handleChange}
									/>
								);
								break;
							case Shared.InputType.InputNumber:
								metaComponent = (
									<InputNumberComponent
										key={metaValue.guid + index + "number"}
										model={metaValue.valueNumber}
										locale={
											AppStore.context.sessionDetails
												.uiLanguage
										}
										onChange={this.handleChange}
									/>
								);
								break;
							case Shared.InputType.InputChoice:
								metaComponent = (
									<InputChoiseComponent
										key={metaValue.guid + index + "choice"}
										model={metaValue.valueChoice}
										testId={
											metaValue.valueText
												? metaValue.valueText.value
												: ""
										}
										onChange={this.handleChange}
									/>
								);
								break;
							case Shared.InputType.InputDate:
								metaComponent = (
									<InputDateComponent
										key={metaValue.guid + index + "date"}
										model={metaValue.valueDate}
										locale={
											AppStore.context.sessionDetails
												.uiLanguage
										}
										clearable={false}
										onChange={this.handleChange}
									/>
								);
								break;
							case Shared.InputType.InputHierarchy:
								metaComponent = (
									<InputHierarchyComponent
										key={metaValue.guid + index + "date"}
										model={metaValue.valueHierarchy}
										documentStore={this.store}
										onChange={() =>
											(metaValue.isValid = true)
										}
									/>
								);
								break;
							case Shared.InputType.InputDocumentLink:
								metaComponent = (
									<InputDocumentLinkComponent
										key={metaValue.guid + index + "date"}
										model={metaValue.valueDocumentLink}
										documentStore={this.store}
										onChange={() =>
											(metaValue.isValid = true)
										}
										selectedLanguage={
											this.props.selectedLanguage
										}
										onUpdateValue={this.onUpdateValue}
										onClick={() =>
											(this.store.isDocumentLinkMultiple =
												this.props.meta.multiple)
										}
									/>
								);
								break;
							default:
								throw "unknown InputType";
						}
						return (
							<li
								key={
									"metaValue_component" +
									this.props.meta.metaGuid +
									index.toString()
								}
								className={
									"various-value__item" +
									(this.props.meta.inputType ===
									Shared.InputType.InputDocumentLink
										? " documentLink"
										: "")
								}
								data-test-id={this.props.testId}
								data-test-name={this.props.testName}
								onClick={() => this.clickValue(metaValue)}
							>
								<SelectCheckbox
									checked={this.isSelected}
									disabled={true}
								/>
								{metaComponent}
								<span className="various-value__item__additional">
									<div className="tinytool">
										{this.props.meta.inputType ===
										Shared.InputType.InputDocumentLink ? (
											<EditButton
												title={
													Strings.documentManagement
														.documentEdit
														.metasEditDocumentButtonTooltip
												}
												onClick={() =>
													this.handleEditDocument(
														metaValue
															.valueDocumentLink
															.value,
														metaValue
															.valueDocumentLink
															.collectionId
													)
												}
												disabled={
													!this.isNew &&
													!metaValue.valueDocumentLink
														.collectionId
												}
											/>
										) : null}
										<CloseButton
											title={
												Strings.documentManagement
													.documentEdit
													.metasRemoveButtonTooltip
											}
											key={
												"delete_meta-" +
												this.props.meta.metaGuid +
												index.toString()
											}
											onClick={this.handleDeleteMeta.bind(
												this,
												metaValue
											)}
										/>
									</div>
								</span>
							</li>
						);
					})}
				</ul>
			</fieldset>
		);
	}
}
